import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const ROOT_SELECTOR = "[data-gsap-root]";
const MEDIA_QUERY = "(min-width: 768px)";
const MEDIA_TARGET_SELECTOR = [
    "img",
    "picture",
    "svg",
    "video",
    ".swiper",
    ".splide",
    '[class*="__media"]',
    '[class*="__image"]',
    '[class*="__photo"]',
].join(", ");

gsap.registerPlugin(ScrollTrigger);

function toArray(elements) {
    return Array.from(elements || []);
}

function isVisibleElement(element) {
    if (!element || !element.tagName) {
        return false;
    }

    if (["SCRIPT", "STYLE", "NOSCRIPT", "LINK"].includes(element.tagName)) {
        return false;
    }

    const styles = window.getComputedStyle(element);

    return styles.display !== "none" && styles.visibility !== "hidden";
}

function uniqueElements(elements) {
    return [...new Set(elements.filter(isVisibleElement))];
}

function belongsToRoot(element, root) {
    return element.closest(ROOT_SELECTOR) === root;
}

function isTopLevelRoot(root) {
    return !root.parentElement || !root.parentElement.closest(ROOT_SELECTOR);
}

function expandAnimationTarget(target) {
    if (!target || !target.matches) {
        return [];
    }

    if (target.matches(".swiper, .splide")) {
        const track = target.querySelector(".swiper-wrapper, .splide__list");

        if (track) {
            const slides = toArray(track.children).filter(isVisibleElement);

            if (slides.length > 1) {
                return slides;
            }
        }
    }

    if (
        target.matches(
            '.swiper-wrapper, .splide__list, .c-accordion, [class*="__cards"], [class*="__list"], [class*="__grid"]',
        )
    ) {
        const children = toArray(target.children).filter(isVisibleElement);

        if (children.length > 1) {
            return children;
        }
    }

    return [target];
}

function getAnimationTargets(root) {
    const manualTargets = toArray(
        root.querySelectorAll("[data-gsap-item]"),
    ).filter((element) => belongsToRoot(element, root));

    if (manualTargets.length) {
        return manualTargets;
    }

    const containerChildren = toArray(
        root.querySelectorAll(":scope > .s-container > *"),
    ).filter((element) => belongsToRoot(element, root));
    const directChildren = toArray(root.children).filter(
        (element) => !element.classList.contains("s-container"),
    );
    const baseTargets = uniqueElements([
        ...containerChildren,
        ...directChildren,
    ]);
    const expandedTargets = [];

    baseTargets.forEach((target) => {
        expandAnimationTarget(target).forEach((element) => {
            if (belongsToRoot(element, root)) {
                expandedTargets.push(element);
            }
        });
    });

    const uniqueTargets = uniqueElements(expandedTargets);

    return uniqueTargets.length ? uniqueTargets : [root];
}

function splitTargets(targets) {
    const contentTargets = [];
    const mediaTargets = [];

    targets.forEach((target) => {
        if (target.matches && target.matches(MEDIA_TARGET_SELECTOR)) {
            mediaTargets.push(target);
            return;
        }

        contentTargets.push(target);
    });

    return {
        contentTargets,
        mediaTargets,
    };
}

function createSectionAnimation(root) {
    const targets = getAnimationTargets(root);
    const { contentTargets, mediaTargets } = splitTargets(targets);
    const timeline = gsap.timeline({
        defaults: {
            ease: "power2.out",
        },
        scrollTrigger: {
            trigger: root,
            start: "top 85%",
            once: true,
        },
    });

    if (contentTargets.length) {
        timeline.from(contentTargets, {
            autoAlpha: 0,
            y: 32,
            duration: 0.75,
            stagger: 0.12,
            clearProps: "opacity,visibility,transform",
        });
    }

    if (mediaTargets.length) {
        timeline.from(
            mediaTargets,
            {
                autoAlpha: 0,
                y: 48,
                scale: 0.96,
                duration: 0.9,
                stagger: 0.12,
                clearProps: "opacity,visibility,transform",
            },
            contentTargets.length ? 0.1 : 0,
        );
    }

    if (!contentTargets.length && !mediaTargets.length) {
        timeline.from(root, {
            autoAlpha: 0,
            y: 32,
            duration: 0.8,
            clearProps: "opacity,visibility,transform",
        });
    }
}

function initAnimations() {
    const roots = toArray(document.querySelectorAll(ROOT_SELECTOR)).filter(
        (root) => isTopLevelRoot(root),
    );

    if (!roots.length) {
        return;
    }

    roots.forEach((root) => createSectionAnimation(root));
}

export default function gsapAnimation() {
    const setupAnimations = () => {
        const mediaMatch = gsap.matchMedia();

        mediaMatch.add(MEDIA_QUERY, () => {
            initAnimations();

            return () => {
                ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
                gsap.set(ROOT_SELECTOR, {
                    clearProps: "all",
                });
                gsap.set(`${ROOT_SELECTOR} *`, {
                    clearProps: "opacity,visibility,transform",
                });
            };
        });
    };

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", setupAnimations, {
            once: true,
        });
        return;
    }

    setupAnimations();
}
